//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { useUrlState } from 'hooks/useUrlState';
import { useEffect, useMemo, useRef, useState } from 'react';

type UseQueryParamsProps = {
  key: string[];
  baseUrl: string;
  splitParamsContainer?: { keyParam: string; splitParams: string[] };
  additionalQueryParams?: { key: string; value: string | number }[];
  queryParamsToInclude?: string[];
};

/**
 * A custom hook to construct query parameters for fetching from the URL.
 *
 * @param {string[]} key - A unique query key array for caching and refetching.
 * @param {string} baseUrl - The base URL of the API endpoint.
 * @param {{key: string; value: string | number}[]} additionalQueryParams - Query params to add to the URL.
 * @param {string[]} queryParamsToInclude - An array of validated params in the URL to get a correct query.
 * @returns An object containing the fetch URL, query key, query parameters, paramsChanged (a tracker for when filter parameters changed)
 *  and resetParamsChanges.
 */
export const useQueryParams = ({
  key,
  baseUrl,
  splitParamsContainer,
  additionalQueryParams,
  queryParamsToInclude,
}: UseQueryParamsProps) => {
  const { getUrlState } = useUrlState();

  const urlState = useMemo(() => getUrlState(), [getUrlState]);

  // Construct the query string from URL state
  const queryParams = useMemo(() => {
    return new URLSearchParams(urlState).toString();
  }, [urlState]);

  const cleanedParams = useMemo(() => {
    const params = new URLSearchParams(queryParams);

    if (queryParamsToInclude) {
      Array.from(params.keys()).forEach((key) => {
        if (!queryParamsToInclude.includes(key)) {
          params.delete(key);
        }
      });
    }

    if (additionalQueryParams) {
      additionalQueryParams.forEach(({ key, value }) => {
        params.append(key, value.toString());
      });
    }

    return params;
  }, [queryParams, additionalQueryParams, queryParamsToInclude]);

  // Combine base URL and query parameters for fetching
  const queryUrl = useMemo(() => {
    const queryString = cleanedParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [cleanedParams, baseUrl]);

  // Create a stable query key for React Query with just the parameters
  const queryKey = useMemo(() => {
    return [...key, cleanedParams.toString()];
  }, [key, cleanedParams]);

  // Split query URL logic for cases when we need to do seperate requests for certain params
  const splitQueryUrls = useMemo(() => {
    if (!splitParamsContainer) {
      return [];
    }

    const { keyParam, splitParams } = splitParamsContainer;
    const [base, queryString] = queryUrl.split('?');
    if (!queryString || !queryString.includes(keyParam)) {
      return [];
    }

    const queryParamsArray = queryString.split('&');

    // Combine keyParam and splitParams dynamically
    const combinedSplitParams = [...splitParams, keyParam];

    const filteredSplitParams = queryParamsArray.filter((param) =>
      combinedSplitParams.some((filter) => param.startsWith(filter))
    );

    const remainingParams = queryParamsArray.filter(
      (param) => !combinedSplitParams.some((filter) => param.startsWith(filter))
    );

    if (filteredSplitParams.length > 0 && remainingParams.length > 0) {
      return [`${base}?${filteredSplitParams.join('&')}`, `${base}?${remainingParams.join('&')}`];
    }

    return [];
  }, [queryUrl, splitParamsContainer]);

  // Track changes to query parameters
  const previousParams = useRef(queryParams);
  const [paramsChanged, setParamsChanged] = useState(false);

  useEffect(() => {
    if (previousParams.current !== queryParams) {
      setParamsChanged(true);
      previousParams.current = queryParams;
    } else {
      setParamsChanged(false);
    }
  }, [queryParams]);

  const resetParamsChanged = () => setParamsChanged(false);

  return {
    queryUrl,
    queryKey,
    queryParams,
    splitQueryUrls,
    paramsChanged,
    resetParamsChanged,
  };
};
